// Business page card: the saved price list (one-time items and monthly plans)
// and each item's public buy link. Shown only to people who can manage
// receivables; anyone else (403) or a location without the plan (402) sees nothing.
import React, { useCallback, useEffect, useState } from 'react';
import apiClient from '../../shared/services/apiClient';
import { formatApiError } from '../../utils/errorMessage';

export const catalogAPI = {
  list: (businessId) => apiClient.get(`/client-portal/${businessId}/catalog`),
  create: (businessId, body) => apiClient.post(`/client-portal/${businessId}/catalog`, body),
  update: (businessId, id, body) => apiClient.patch(`/client-portal/${businessId}/catalog/${id}`, body),
};

const money = (n, c) => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: (c || 'usd').toUpperCase() }).format(Number(n) || 0); } catch (e) { return `$${Number(n || 0).toFixed(2)}`; }
};
const input = 'border rounded px-2 py-1 text-sm';
const EMPTY = { name: '', slug: '', description: '', billing: 'one_time', price: '', is_setup_fee: false, setup_fee_item_id: '', term_discounts: [], buy_link_enabled: false, active: true };

function toForm(item) {
  return { ...EMPTY, ...item, description: item.description || '', setup_fee_item_id: item.setup_fee_item_id || '', term_discounts: item.term_discounts || [] };
}

export function ItemForm({ initial, fees, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setTerm = (i, k, v) => set('term_discounts', f.term_discounts.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  const submit = (e) => {
    e.preventDefault();
    onSave({
      name: f.name, slug: f.slug || undefined, description: f.description, billing: f.billing, price: f.price,
      is_setup_fee: f.billing === 'one_time' && f.is_setup_fee,
      setup_fee_item_id: f.billing === 'monthly' && f.setup_fee_item_id ? Number(f.setup_fee_item_id) : null,
      term_discounts: f.billing === 'one_time' && f.is_setup_fee ? f.term_discounts.map((t) => ({ months: Number(t.months), percent_off: Number(t.percent_off) })) : [],
      buy_link_enabled: f.buy_link_enabled, active: f.active,
    });
  };
  return (
    <form onSubmit={submit} className="border rounded p-3 space-y-2" aria-label="Price list item">
      <div className="flex flex-wrap gap-2">
        <input className={input} aria-label="Name" placeholder="Name" required value={f.name} onChange={(e) => set('name', e.target.value)} />
        <input className={input} aria-label="Link name" placeholder="link-name (optional)" value={f.slug} onChange={(e) => set('slug', e.target.value.toLowerCase())} />
        <select className={input} aria-label="Billing" value={f.billing} onChange={(e) => set('billing', e.target.value)}>
          <option value="one_time">One-time</option><option value="monthly">Monthly</option>
        </select>
        <input className={input} aria-label="Price" placeholder="Price" required inputMode="decimal" value={f.price} onChange={(e) => set('price', e.target.value)} />
      </div>
      <textarea className={`${input} w-full`} aria-label="Description" placeholder="Description (shown on the buy page)" value={f.description} onChange={(e) => set('description', e.target.value)} />
      {f.billing === 'one_time' && (
        <label className="block text-sm"><input type="checkbox" checked={f.is_setup_fee} onChange={(e) => set('is_setup_fee', e.target.checked)} /> This is a setup fee</label>
      )}
      {f.billing === 'one_time' && f.is_setup_fee && (
        <div className="text-sm space-y-1">
          <p className="font-medium">Term discounts (optional)</p>
          {f.term_discounts.map((t, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className={`${input} w-20`} aria-label="Term months" inputMode="numeric" value={t.months} onChange={(e) => setTerm(i, 'months', e.target.value)} /> months:
              <input className={`${input} w-20`} aria-label="Percent off" inputMode="decimal" value={t.percent_off} onChange={(e) => setTerm(i, 'percent_off', e.target.value)} />% off
              <button type="button" className="underline" onClick={() => set('term_discounts', f.term_discounts.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
          <button type="button" className="underline" onClick={() => set('term_discounts', [...f.term_discounts, { months: '', percent_off: '' }])}>Add a term discount</button>
          <p className="text-gray-600">Use 100% off to waive the fee for that term.</p>
        </div>
      )}
      {f.billing === 'monthly' && (
        <label className="block text-sm">Setup fee billed with the first month:{' '}
          <select className={input} aria-label="Setup fee" value={f.setup_fee_item_id} onChange={(e) => set('setup_fee_item_id', e.target.value)}>
            <option value="">None</option>
            {fees.map((x) => <option key={x.id} value={x.id}>{x.name} ({money(x.price, x.currency)})</option>)}
          </select>
        </label>
      )}
      <label className="block text-sm"><input type="checkbox" checked={f.buy_link_enabled} onChange={(e) => set('buy_link_enabled', e.target.checked)} /> Public buy link on</label>
      <label className="block text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> Active</label>
      <div className="flex gap-2"><button type="submit" className="border rounded px-3 py-1">Save</button>{onCancel && <button type="button" className="underline" onClick={onCancel}>Cancel</button>}</div>
    </form>
  );
}

export default function CatalogSettings({ businessId }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try { setData((await catalogAPI.list(businessId)).data); } catch (e) {
      const s = e && e.response && e.response.status;
      if (s === 403 || s === 402 || s === 401) setHidden(true); else setError(formatApiError(e, 'Could not load the price list'));
    }
  }, [businessId]);
  useEffect(() => { load(); }, [load]);

  if (hidden) return null;
  if (!data) return error ? <p role="alert" className="text-red-600 text-sm">{error}</p> : null;
  const fees = data.items.filter((i) => i.is_setup_fee && i.active);
  const save = async (body) => {
    setError(''); setNote('');
    try {
      const r = editing === 'new' ? await catalogAPI.create(businessId, body) : await catalogAPI.update(businessId, editing.id, body);
      if (r.data && r.data.unchanged) setNote('No changes to save.');
      setEditing(null); await load();
    } catch (e) { setError(formatApiError(e, 'Could not save the item')); }
  };
  return (
    <section className="space-y-2" aria-label="Price list" data-testid="catalog-settings">
      <h3 className="font-semibold">Price list and buy links</h3>
      {!data.buy_links_ready && <p className="text-sm text-gray-600">{data.buy_links_note}</p>}
      {error && <p role="alert" className="text-red-600 text-sm">{error}</p>}
      {note && <p className="text-sm text-gray-700">{note}</p>}
      {data.items.length === 0 ? <p className="text-sm text-gray-600">No items yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr><th className="text-left">Item</th><th className="text-left">Billing</th><th className="text-right">Price</th><th className="text-left">Buy link</th><th /></tr></thead>
          <tbody>{data.items.map((i) => (
            <tr key={i.id} className={i.active ? '' : 'text-gray-400'}>
              <td>{i.name}{i.is_setup_fee ? ' (setup fee)' : ''}{(i.term_discounts || []).length ? ` - ${i.term_discounts.map((t) => `${t.months} mo: ${Number(t.percent_off) >= 100 ? 'waived' : `${t.percent_off}% off`}`).join(', ')}` : ''}</td>
              <td>{i.billing === 'monthly' ? 'Monthly' : 'One-time'}</td>
              <td className="text-right">{money(i.price, i.currency)}</td>
              <td>{i.buy_link_enabled && i.active && i.buy_url ? <a className="underline break-all" href={i.buy_url} target="_blank" rel="noreferrer">{i.buy_url}</a> : 'Off'}</td>
              <td><button type="button" className="underline" onClick={() => setEditing(i)}>Edit</button></td>
            </tr>))}</tbody></table></div>
      )}
      {editing ? <ItemForm key={editing === 'new' ? 'new' : editing.id} initial={editing === 'new' ? EMPTY : toForm(editing)} fees={fees.filter((x) => editing === 'new' || x.id !== editing.id)} onSave={save} onCancel={() => setEditing(null)} />
        : <button type="button" className="border rounded px-3 py-1 text-sm" onClick={() => setEditing('new')}>Add an item</button>}
    </section>
  );
}
